/* La Sura - reference photos in the cake builder.

   Each photo is its own file input tied to the product form with form=, so
   the photos post with or without this script. This element adds: one slot
   at a time ("Add another photo"), a preview of each photo, a remove button,
   and a refusal, before add to basket, of a file that isn't an image or is
   over the size limit. When the first slot is required, a missed photo is
   named under the slot (data-required-text) in place of the browser's
   bubble, and focus goes to it. */
class LasuraCakeReference extends HTMLElement {
  connectedCallback() {
    this.slots = Array.from(this.querySelectorAll('[data-ref-slot]'));
    this.addButton = this.querySelector('[data-ref-add]');
    this.slots.forEach((slot, i) => (slot.hidden = i > 0));
    /* An empty file input still posts a blank field, so a slot only carries
       its name while it holds a photo. */
    this.querySelectorAll('[data-ref-input]').forEach((input) => {
      input.dataset.name = input.name;
      if (!input.files || !input.files.length) input.removeAttribute('name');
    });

    if (this.addButton) {
      this.addButton.hidden = this.slots.length < 2;
      this.addButton.addEventListener('click', () => this.revealNext());
    }
    this.querySelectorAll('[data-ref-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const slot = btn.closest('[data-ref-slot]');
        const input = slot.querySelector('[data-ref-input]');
        input.value = '';
        this.show(slot, null);
        input.focus();
      });
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-ref-input]')) this.onPick(event.target);
    });
    /* invalid doesn't bubble, so catch it on the way down. */
    if (this.dataset.requiredText) this.addEventListener('invalid', (event) => this.onInvalid(event), true);
    this.requiredInput = this.querySelector('[data-ref-input][required]');
  }

  /* Required means one photo in any slot: photo 2 left after photo 1 is
     removed still counts. */
  syncRequired() {
    if (!this.requiredInput) return;
    const inputs = Array.from(this.querySelectorAll('[data-ref-input]'));
    this.requiredInput.required = !inputs.some((input) => input.files && input.files.length);
  }

  onInvalid(event) {
    const input = event.target;
    if (!input.matches('[data-ref-input]')) return;
    event.preventDefault();
    const status = input.closest('[data-ref-slot]').querySelector('[data-ref-status]');
    status.textContent = this.dataset.requiredText;
    status.setAttribute('data-error', '');
    input.setAttribute('aria-invalid', 'true');
    /* Add to basket checks the form twice (its click, then the submit), so
       only the first of a burst moves the page. */
    const now = performance.now();
    if (now - (this.invalidAt || 0) < 400) return;
    this.invalidAt = now;
    const details = this.closest('details');
    if (details && !details.open) details.open = true;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* instant, not auto: auto follows the theme's CSS scroll-behavior, which is smooth. */
    this.scrollIntoView({ block: 'center', behavior: reduce ? 'instant' : 'smooth' });
    input.focus({ preventScroll: true });
  }

  revealNext() {
    const next = this.slots.find((slot) => slot.hidden);
    if (!next) return;
    next.hidden = false;
    next.querySelector('[data-ref-input]').focus();
    this.addButton.hidden = !this.slots.some((slot) => slot.hidden);
  }

  isHeic(file) {
    return file.type === 'image/heic' || file.type === 'image/heif' || /\.(heic|heif)$/i.test(file.name);
  }

  onPick(input) {
    const slot = input.closest('[data-ref-slot]');
    const status = slot.querySelector('[data-ref-status]');
    const file = input.files && input.files[0];
    status.removeAttribute('data-error');
    input.removeAttribute('aria-invalid');
    if (!file) return this.show(slot, null);

    const maxMb = Number(this.dataset.maxMb) || 10;
    let problem = '';
    if (!file.type.startsWith('image/') && !this.isHeic(file)) problem = this.dataset.notImage;
    else if (file.size > maxMb * 1024 * 1024) problem = (this.dataset.tooBig || '').split('{size}').join(maxMb);

    if (problem) {
      input.value = '';
      this.show(slot, null);
      status.textContent = problem;
      status.setAttribute('data-error', '');
      return;
    }
    this.show(slot, file);
  }

  show(slot, file) {
    const preview = slot.querySelector('[data-ref-preview]');
    const status = slot.querySelector('[data-ref-status]');
    const remove = slot.querySelector('[data-ref-remove]');
    const input = slot.querySelector('[data-ref-input]');
    if (file) input.name = input.dataset.name;
    else input.removeAttribute('name');
    this.syncRequired();
    if (preview.src) URL.revokeObjectURL(preview.src);
    preview.removeAttribute('src');
    preview.hidden = true;
    remove.hidden = !file;
    if (!file) {
      if (!status.hasAttribute('data-error')) status.textContent = '';
      return;
    }
    /* HEIC won't preview in most browsers; the file name stands in for it. */
    if (!this.isHeic(file)) {
      preview.src = URL.createObjectURL(file);
      preview.hidden = false;
    }
    status.textContent = file.name;
  }
}

if (!customElements.get('lasura-cake-reference')) {
  customElements.define('lasura-cake-reference', LasuraCakeReference);
}
